export const POLICY_MAKER_PROFILE_KEY = 'stratosphere_policy_maker_profile';

export const FOCUS_MINERALS = [
  'Lithium',
  'Cobalt',
  'Nickel',
  'Graphite',
  'Rare Earth',
];

export const PRIMARY_MANDATES = [
  'Supply Security',
  'Domestic Production',
  'Bilateral Agreements',
  'All',
];

export const RESPONSIBLE_SECTORS = [
  'EV',
  'Solar',
  'Defence',
  'Semiconductors',
  'Fertilisers',
  'All',
];

export const PLANNING_HORIZONS = ['2027', '2030', '2035'];

export const BRIEFING_TRIGGERS = [
  'China export restriction',
  'MoU status change',
  'Peer country signs competing deal',
  'Domestic auction delay',
  'All',
];

export const BRIEF_FORMATS = [
  'Daily one-pager',
  'Weekly digest',
  'Event-triggered only',
];

/** Public-record MoUs / KABIL engagements — user confirms or adds more */
export const PRELOADED_MOUS = [
  {
    id: 'mou-au-lithium',
    label: 'Australia — critical minerals MoU (lithium supply cooperation)',
    country: 'Australia',
    minerals: ['Lithium'],
    source: 'Public record',
  },
  {
    id: 'mou-ar-camyen',
    label: 'Argentina — CAMYEN / KABIL lithium exploration framework',
    country: 'Argentina',
    minerals: ['Lithium'],
    source: 'Public record',
  },
  {
    id: 'mou-zm-copper',
    label: 'Zambia — KABIL copper-cobalt joint venture (Lubambe / Konkola corridor)',
    country: 'Zambia',
    minerals: ['Copper', 'Cobalt'],
    source: 'Public record',
  },
  {
    id: 'mou-au-ree',
    label: 'Australia — REE & critical minerals partnership (downstream processing)',
    country: 'Australia',
    minerals: ['Rare Earth'],
    source: 'Public record',
  },
  {
    id: 'mou-cl-lithium',
    label: 'Chile — lithium offtake / supply dialogue (bilateral track)',
    country: 'Chile',
    minerals: ['Lithium'],
    source: 'Public record',
  },
];

/** Scheduled / announced domestic block auctions — user flags oversight */
export const PRELOADED_BLOCK_AUCTIONS = [
  {
    id: 'auction-jk-lithium',
    label: 'Jammu & Kashmir — Reasi / Salal-Haimana lithium blocks (G3 auction tranche)',
    state: 'Jammu & Kashmir',
    mineral: 'Lithium',
    source: 'Public record',
  },
  {
    id: 'auction-od-graphite',
    label: 'Odisha — graphite block auctions (critical mineral notified blocks)',
    state: 'Odisha',
    mineral: 'Graphite',
    source: 'Public record',
  },
  {
    id: 'auction-cg-lithium',
    label: 'Chhattisgarh — Korba lithium block (exploration licence route)',
    state: 'Chhattisgarh',
    mineral: 'Lithium',
    source: 'Public record',
  },
  {
    id: 'auction-ka-nickel',
    label: 'Karnataka — nickel / PGE notified blocks (auction pipeline)',
    state: 'Karnataka',
    mineral: 'Nickel',
    source: 'Public record',
  },
];

export function createMouSelections() {
  return PRELOADED_MOUS.map((m) => ({
    id: m.id,
    confirmed: true,
    notes: '',
  }));
}

export function createAuctionSelections() {
  return PRELOADED_BLOCK_AUCTIONS.map((a) => ({
    id: a.id,
    inOversight: false,
    notYetPublic: false,
    notes: '',
  }));
}

export function initialPolicyMakerFormState() {
  return {
    ministryDepartmentName: '',
    policyRole: '',
    primaryMandate: '',
    priorityMinerals: [],
    responsibleSectors: [],
    planningHorizon: '',
    mouSelections: createMouSelections(),
    additionalMous: '',
    auctionSelections: createAuctionSelections(),
    additionalAuctions: '',
    briefingTriggers: [],
    briefFormat: '',
  };
}

export function loadPolicyMakerProfile() {
  try {
    const saved = localStorage.getItem(POLICY_MAKER_PROFILE_KEY);
    if (!saved) return null;
    const parsed = JSON.parse(saved);
    const base = initialPolicyMakerFormState();
    return {
      ...base,
      ...parsed,
      priorityMinerals: parsed.priorityMinerals?.length
        ? parsed.priorityMinerals
        : base.priorityMinerals,
      responsibleSectors: parsed.responsibleSectors?.length
        ? parsed.responsibleSectors
        : base.responsibleSectors,
      briefingTriggers: parsed.briefingTriggers?.length
        ? parsed.briefingTriggers
        : base.briefingTriggers,
      mouSelections: mergeMouSelections(parsed.mouSelections),
      auctionSelections: mergeAuctionSelections(parsed.auctionSelections),
    };
  } catch {
    return null;
  }
}

function mergeMouSelections(saved) {
  const byId = Object.fromEntries((saved || []).map((s) => [s.id, s]));
  return PRELOADED_MOUS.map((m) => ({
    id: m.id,
    confirmed: byId[m.id]?.confirmed ?? true,
    notes: byId[m.id]?.notes ?? '',
  }));
}

function mergeAuctionSelections(saved) {
  const byId = Object.fromEntries((saved || []).map((s) => [s.id, s]));
  return PRELOADED_BLOCK_AUCTIONS.map((a) => ({
    id: a.id,
    inOversight: byId[a.id]?.inOversight ?? false,
    notYetPublic: byId[a.id]?.notYetPublic ?? false,
    notes: byId[a.id]?.notes ?? '',
  }));
}

export function savePolicyMakerProfile(profile) {
  localStorage.setItem(POLICY_MAKER_PROFILE_KEY, JSON.stringify(profile));
}

export function getPreloadedMouById(id) {
  return PRELOADED_MOUS.find((m) => m.id === id);
}

export function getPreloadedAuctionById(id) {
  return PRELOADED_BLOCK_AUCTIONS.find((a) => a.id === id);
}
